import { Link } from 'react-router-dom';
import { formatAccountingDate } from '../ui';
import { formatMoney, originLinks, sourceBadge, statusLabel } from './sourceLabels';

type Entry = {
  id: string;
  entryNumber?: number | null;
  entryDate: string;
  description: string;
  status: string;
  sourceType?: string;
  sourceId?: string | null;
  sourceKey?: string | null;
  metadata?: { reference?: string } | null;
  totalDebit?: string | number;
  totalCredit?: string | number;
  balanced?: boolean;
  branch?: { code: string; name: string } | null;
  createdBy?: { firstName?: string; lastName?: string } | null;
  lines?: Array<{
    id: string;
    description?: string | null;
    debit: string | number;
    credit: string | number;
    account?: { code: string; name: string };
    branch?: { code: string; name: string } | null;
    costCenter?: { code: string; name: string } | null;
  }>;
};

type Props = {
  entry: Entry | null;
  onClose: () => void;
};

export function JournalEntryDrawer({ entry, onClose }: Props) {
  if (!entry) return null;

  const badge = sourceBadge(entry.sourceType);
  const debit = Number(entry.totalDebit ?? 0);
  const credit = Number(entry.totalCredit ?? 0);
  const diff = Math.abs(debit - credit);
  const balanced = entry.balanced ?? diff < 0.005;
  const user = entry.createdBy
    ? `${entry.createdBy.firstName ?? ''} ${entry.createdBy.lastName ?? ''}`.trim()
    : '—';
  const reference =
    entry.metadata?.reference || entry.sourceKey || entry.sourceId || '—';
  const links = originLinks(entry);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/30"
        aria-label="Cerrar"
        onClick={onClose}
      />
      <aside className="relative z-10 flex h-full w-full max-w-lg flex-col bg-white shadow-xl sm:max-w-xl">
        <header className="border-b px-4 py-3 sm:px-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Asiento #{entry.entryNumber ?? 'BORRADOR'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {formatAccountingDate(entry.entryDate)} · {statusLabel(entry.status)}
            </p>
          </div>
          <button type="button" className="text-gray-500 hover:text-gray-800 text-xl leading-none" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 space-y-4">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <dt className="text-xs text-gray-500">Concepto</dt>
              <dd className="font-medium text-gray-900">{entry.description}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Origen</dt>
              <dd>
                <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${badge.tone}`}>
                  {badge.label}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Usuario</dt>
              <dd>{user || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Sucursal</dt>
              <dd>
                {entry.branch ? `${entry.branch.code} · ${entry.branch.name}` : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Referencia</dt>
              <dd className="font-mono text-xs break-all">{reference}</dd>
            </div>
          </dl>

          {links.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-1.5 text-sm rounded-md border bg-gray-50 hover:bg-gray-100"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          )}

          <div className="overflow-x-auto rounded-md border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Cuenta</th>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2 text-right">Debe</th>
                  <th className="px-3 py-2 text-right">Haber</th>
                  <th className="px-3 py-2">Sucursal</th>
                  <th className="px-3 py-2">Centro</th>
                </tr>
              </thead>
              <tbody>
                {(entry.lines ?? []).map((l) => (
                  <tr key={l.id} className="border-t">
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs text-gray-500">{l.account?.code}</span>
                      <div>{l.account?.name}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{l.description || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {Number(l.debit) ? formatMoney(l.debit) : ''}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {Number(l.credit) ? formatMoney(l.credit) : ''}
                    </td>
                    <td className="px-3 py-2 text-xs">{l.branch?.code ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{l.costCenter?.code ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="border-t px-4 py-3 sm:px-5 space-y-2 bg-gray-50">
          <div className="flex flex-wrap justify-between gap-2 text-sm tabular-nums">
            <span>Total Debe: <strong>{formatMoney(debit)}</strong></span>
            <span>Total Haber: <strong>{formatMoney(credit)}</strong></span>
            <span>
              Diferencia:{' '}
              <strong className={diff < 0.005 ? 'text-emerald-700' : 'text-red-700'}>
                {formatMoney(diff)}
              </strong>
            </span>
          </div>
          {balanced ? (
            <p className="text-sm text-emerald-700 font-medium">✓ Asiento balanceado</p>
          ) : (
            <p className="text-sm text-red-700 font-medium">✕ Asiento desbalanceado</p>
          )}
        </footer>
      </aside>
    </div>
  );
}
